import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { type GridRowSelectionModel } from "@mui/x-data-grid";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useMemo, useState } from "react";
import { Link, Outlet, useParams } from "react-router-dom";

import { DjangoPaginationAdapter } from "api/common/adapters/pagination.adapters";
import { TableQueryParams } from "api/common/adapters/pagination.types";
import { createQueryAdapter } from "api/common/adapters/queryParam.adapter";
import {
  useGetMailListQuery,
  useListMailListUsersQuery,
} from "api/memberPortal/mailList/mailList.api";
import SearchBar from "components/MUI/DataGrid/Detached/SearchBar";
import BackButton from "components/UI/BackButton/BackButton";

import { DetailCardMode } from "./components/MailListDetailsCard/MailListDetails.types";
import MailListDetailsCard from "./components/MailListDetailsCard/MailListDetailsCard";
import MailListDetailsMembersTable from "./components/MemberTable/MailListDetailsMembers.table";

const memberApiAdapter = createQueryAdapter({
  paginationAdapter: DjangoPaginationAdapter,
});

const EMPTY_PAGINATED_DATA = { results: [], count: 0 };

export default function MailListDetails() {
  const { id } = useParams();

  const {
    data: mailListData,
    isLoading,
    error,
  } = useGetMailListQuery(id ?? skipToken);

  const [tableParams, setTableParams] = useState<TableQueryParams>({
    page: 0,
    pageSize: 100,
    sort: { field: "dateAdded", direction: "desc" },
    search: "",
  });
  const [mode, setMode] = useState<DetailCardMode>("view");
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);

  const memberQueryParams = useMemo(() => {
    return memberApiAdapter.toTableParams({
      pagination: tableParams,
    });
  }, [tableParams]);

  const {
    data: memberListData = EMPTY_PAGINATED_DATA,
    isLoading: isMemberLoading,
  } = useListMailListUsersQuery(
    id ? { listId: id, ...memberQueryParams } : skipToken,
  );

  const handleSearchChange = useCallback((value: string) => {
    setTableParams((prev) => ({ ...prev, search: value, page: 0 }));
  }, []);

  if (isLoading) return <CircularProgress />;
  if (error || !mailListData) {
    return (
      <Box sx={{ my: 5, px: 3 }}>
        <BackButton to=".." label="Mailing Lists" />
        <Alert severity="error" sx={{ mt: 3 }}>
          This mailing list could not be found. It may have been deleted.
        </Alert>
      </Box>
    );
  }

  const { name, description, category, lastModified, email } = mailListData;

  const handleClearTable = () => {
    setSelectedRows([]);
  };
  const handleAddMemberClick = () => {
    setMode("view");
    setSelectedRows([]);
  };

  return (
    <Box
      sx={{
        my: 5,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <BackButton to=".." label="Mailing Lists" />
      <Stack
        sx={{
          px: 3,
          my: 5,
          maxWidth: "1000px",
          alignSelf: "center",
          width: "100%",
        }}
        gap={3}
      >
        <MailListDetailsCard
          id={mailListData.id}
          domain={mailListData.domain}
          title={name}
          description={description}
          color={category?.themeColor ?? "ink.light"}
          category={category?.id ?? 0}
          memberCount={mailListData.memberCount}
          lastUpdated={lastModified}
          email={email}
          mode={mode}
          setMode={setMode}
          onSelectionClear={handleClearTable}
        />
        <Stack
          direction="row"
          alignItems={"center"}
          gap={2}
          sx={{
            mt: 5,
          }}
        >
          <Typography>Members</Typography>
          <SearchBar
            sx={{ flex: 1, maxWidth: 500 }}
            onSearchChange={handleSearchChange}
          />
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            sx={{ ml: "auto" }}
            component={Link}
            to={"add"}
            onClick={handleAddMemberClick}
          >
            Add Member
          </Button>
        </Stack>
        <MailListDetailsMembersTable
          members={memberListData}
          isLoading={isMemberLoading}
          tableParams={tableParams}
          onTableParamsChange={setTableParams}
          mailListId={Number(id)}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
        />
      </Stack>
      <Outlet />
    </Box>
  );
}
